import clsx from 'clsx'
import { type Dispatch, type SetStateAction, useMemo } from 'react'

import FilterControl from './FilterControl'
import { type FilterConfig, hasFilterValues, isDateFilter } from './types'

type FilterCardProps = {
  filter: FilterConfig
  index: number
  queryString: string
  setQueryString: Dispatch<SetStateAction<string>>
  openDatePicker: string | null
  setOpenDatePicker: Dispatch<SetStateAction<string | null>>
  enabledFilters: Record<string, boolean>
  setEnabledFilters: Dispatch<SetStateAction<Record<string, boolean>>>
}

function getBaseColorClasses(
  filter: FilterConfig,
  hasActualSelections: boolean,
  index: number,
): string {
  if (filter.category === 'date' || isDateFilter(filter.name))
    return 'bg-fv-beer-xlight border-fv-beer/80'
  if (filter.category === 'origin')
    return 'bg-fv-orange-50 border-fv-orange-500/80'
  if (filter.category === 'dest') return 'bg-fv-green-100 border-fv-green-500'
  if (hasActualSelections) return 'bg-fv-blue/15 border-fv-blue-300'

  // Alternate background every 3 cards for visual grouping
  const bgClass = Math.floor(index / 3) % 2 === 0 ? 'bg-fv-blue-25' : 'bg-white'
  return `${bgClass} border-fv-blue-300`
}

export const FilterCard = ({
  filter,
  index,
  queryString,
  setQueryString,
  openDatePicker,
  setOpenDatePicker,
  enabledFilters,
  setEnabledFilters,
}: FilterCardProps) => {
  const params = useMemo(() => new URLSearchParams(queryString), [queryString])
  const hasActualSelections = hasFilterValues(filter, params)

  const hasAnyChecked =
    queryString !== '' || Object.values(enabledFilters).some(Boolean)

  return (
    <div
      key={filter.name}
      className={clsx(
        getBaseColorClasses(filter, hasActualSelections, index),
        hasActualSelections && 'border-2 -m-px',
        'py-1 px-2 rounded-sm border group',
        hasAnyChecked && !hasActualSelections
          ? 'opacity-60 hover:opacity-100 focus-within:opacity-100'
          : 'opacity-100 group',
      )}
    >
      <FilterControl
        {...filter}
        queryString={queryString}
        setQueryString={setQueryString}
        openDatePicker={openDatePicker}
        setOpenDatePicker={setOpenDatePicker}
        setFilterEnabled={(name, enabled) =>
          setEnabledFilters(current => ({
            ...current,
            [name]: enabled,
          }))
        }
      />
    </div>
  )
}
